import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { MHS_MODULES, MLINE_MODULE_TYPES } from 'src/constants/m-line';
import { TRANSACTIONS_WEIGHT_LOSS_THRESHOLD } from 'src/constants/m-line';
import { useAllTransactions } from 'src/hooks/services/useLps';
import { formatThreeDecimalsNumber } from 'src/utils/conversions';

import BuildTrustCycloneForm from './build-trust-cyclone-form';
import GeneralTrustCycloneForm from './general-trust-cyclone-form';

const ModuleWrapper = ({
  module,
  slot,
  trustCyclone,
  inputModuleTransactions,
  handleConfirmLoadFromInput,
  handleRefetchCyclone,
}) => {
  // Determine which component to render based on the module type
  const isBuildModule = module?.type === MLINE_MODULE_TYPES.BUILD;
  const ComponentToRender = isBuildModule ? BuildTrustCycloneForm : GeneralTrustCycloneForm;

  const inputModuleParentTransactionUri = inputModuleTransactions[0]?.parent_transaction;
  const initialAvailableMaterial = formatThreeDecimalsNumber(inputModuleTransactions[0]?.initial_quantity_available) || 0;

  const {
    data: allTransactions = [],
    isInitialLoading: isAllTransactionsLoading,
  } = useAllTransactions(inputModuleParentTransactionUri);

  const reconciledTransactions = allTransactions.filter(t => t.is_complete);
  const pendingTransactions = allTransactions.filter(t => !t.is_complete);

  // Determine if this is the last transaction based on container type
  const isLastTransaction = useMemo(() => {
    if (pendingTransactions.length === 0) return false;

    const lastPendingTransaction = pendingTransactions[pendingTransactions.length - 1];

    // For 'dose' or 'overflow' types, we consider it the last transaction if there's 1 pending transaction
    if ([MLINE_MODULE_TYPES.DOSE, MLINE_MODULE_TYPES.OVERFLOW].includes(lastPendingTransaction.container_type)) {
      return pendingTransactions.length === 1;
    }

    // For 'build' type, we consider it the last transaction if there are 2 pending transactions
    if (lastPendingTransaction.container_type === MLINE_MODULE_TYPES.BUILD) {
      return pendingTransactions.length === 2;
    }

    return false;
  }, [pendingTransactions]);

  // Calculate sum of reconciled transactions
  const sumOfReconciledTransactions = useMemo(() => {
    return reconciledTransactions.reduce((sum, transaction) => {
      return sum + (transaction.quantity || 0);
    }, 0);
  }, [reconciledTransactions]);

  const remainingQty = initialAvailableMaterial - sumOfReconciledTransactions;

  // Calculate percentages
  const remainingQtyPercentage = initialAvailableMaterial > 0
    ? Math.round((remainingQty / initialAvailableMaterial) * 100)
    : 0;

  // Calculate reconciled percentage (how much has been used)
  const reconciledQtyPercentage = initialAvailableMaterial > 0
    ? Math.round((sumOfReconciledTransactions / initialAvailableMaterial) * 100)
    : 0;

  return (
    <ComponentToRender
      module={module}
      slot={slot}
      trustCyclone={trustCyclone}
      inputModuleTransactions={inputModuleTransactions}
      handleConfirmLoadFromInput={handleConfirmLoadFromInput}
      handleRefetchCyclone={handleRefetchCyclone}
      progressBarProps={{
        initialQty: initialAvailableMaterial,
        remainingQtyPercentage,
        remainingQty,
        reconciledQtyPercentage,
        isAllTransactionsLoading,
        initialAvailableMaterial,
        isLastTransaction,
        weightLossThreshold: TRANSACTIONS_WEIGHT_LOSS_THRESHOLD,
      }}
    />
  );
};

ModuleWrapper.propTypes = {
  /* eslint-disable camelcase */
  module: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    tare_weight: PropTypes.number,
    current_batch: PropTypes.string,
  }).isRequired,
  slot: PropTypes.string,
  trustCyclone: PropTypes.bool.isRequired,
  handleConfirmLoadFromInput: PropTypes.func.isRequired,
  inputModuleTransactions: PropTypes.arrayOf(PropTypes.shape({
    parent_transaction: PropTypes.string,
    initial_quantity_available: PropTypes.number,
  })).isRequired,
  handleRefetchCyclone: PropTypes.func.isRequired,
};

ModuleWrapper.defaultProps = {
  slot: MHS_MODULES.INPUT,
};

export default React.memo(ModuleWrapper);
